import { useEffect, useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TableCell, TableRow } from "@/components/ui/table";
import { QualityFlagsPill } from "@/components/QualityFlagsPill";

import {
  scannerResults,
  type ScannerItem,
  type Settings,
} from "@/lib/api";
import { useScanner } from "@/lib/useScanner";
import {
  atrClass,
  hitClass,
  posClass,
  retClass,
  rrClass,
  rsClass,
  tickerTier,
  tierClass,
  trendClass,
  TREND_GLYPHS,
} from "@/lib/colors";
import { dollarCompact, percent, price, ratio, signedPercent } from "@/lib/format";

interface ScannerPanelProps {
  /** Current Settings, or null if not yet loaded. The panel surfaces
   *  Scanner* fields inline (enabled toggle, min-hit-score slider) so
   *  the user doesn't have to open the Settings sheet for common
   *  controls. */
  settings: Settings | null;
  /** Called with a partial Settings patch when the user edits inline
   *  controls. Wiring goes through the existing putSettings flow so
   *  the Settings sheet stays in sync. */
  onSettingsChange: (patch: Partial<Record<string, unknown>>) => Promise<void>;
  /** Promote a ticker to the default watchlist (adds it to
   *  default_watchlist if not already present). */
  onPromote: (ticker: string) => Promise<void>;
  /** Optional: open the ticker detail sheet for a scanner row. App.tsx
   *  wires this to a handler that runs /api/analyze on demand (if the
   *  ticker isn't already in the analyses map) and then opens the
   *  sheet. Undefined leaves rows read-only. */
  onSelect?: (ticker: string) => void;
}

/**
 * Collapsible "Scanner" section for the main App page. Subscribes to
 * the /api/scanner/stream SSE endpoint, shows running/stopped
 * controls, live progress, and a table of found setups. The table
 * starts from an on-load snapshot of /api/scanner/results so users
 * see history from the previous session before the first new hit
 * arrives.
 */
export function ScannerPanel({ settings, onSettingsChange, onPromote, onSelect }: ScannerPanelProps) {
  const [open, setOpen] = useState(false);
  const [minScore, setMinScore] = useState(3);
  const [seedItems, setSeedItems] = useState<ScannerItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Subscribe to the SSE stream only when the panel is open — no point
  // holding the connection while collapsed, since a fresh open will
  // bootstrap us with a status + the live results snapshot.
  const { status, progress, hits, lastError, connecting } = useScanner(open);

  useEffect(() => {
    if (settings?.ScannerMinHitScore !== undefined) {
      setMinScore(settings.ScannerMinHitScore);
    }
  }, [settings?.ScannerMinHitScore]);

  // Load the current snapshot whenever we open the panel or the user
  // changes the min score filter. This is the "history" view — the
  // live `hits` map covers only setups found since the SSE stream
  // connected.
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    void scannerResults({ minScore, limit: 200, sort: "score" })
      .then((r) => setSeedItems(r.items))
      .catch((e: unknown) =>
        setActionError(e instanceof Error ? e.message : String(e)),
      )
      .finally(() => setLoading(false));
  }, [open, minScore]);

  // Merge the SSE hit map over the seeded snapshot so new finds bubble
  // to the top without clobbering what's already there. The merge key
  // is ticker — a hit always wins over a seeded row.
  const mergedItems = useMemo(() => {
    const byTicker: Record<string, ScannerItem> = {};
    for (const it of seedItems) byTicker[it.ticker] = it;
    for (const t in hits) byTicker[t] = hits[t]!;
    const rows = Object.values(byTicker).filter((r) => r.overall_score >= minScore);
    rows.sort((a, b) => {
      if (b.overall_score !== a.overall_score) return b.overall_score - a.overall_score;
      return new Date(b.last_scan).getTime() - new Date(a.last_scan).getTime();
    });
    return rows;
  }, [seedItems, hits, minScore]);

  const running = status?.running ?? settings?.ScannerEnabled ?? false;
  const universeSize = status?.universe_size ?? 0;
  const scanned = progress?.scanned ?? status?.scanned ?? 0;
  const queueDepth = progress?.queueDepth ?? status?.queue_depth ?? 0;

  const handleMinScoreSave = async (n: number) => {
    setMinScore(n);
    try {
      await onSettingsChange({ scanner_min_hit_score: n });
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <button
            type="button"
            className="flex items-center gap-2 text-left"
            onClick={() => setOpen((o) => !o)}
          >
            <span className={`inline-block transition-transform ${open ? "rotate-90" : ""}`}>
              ▸
            </span>
            <CardTitle>Scanner</CardTitle>
            <Badge variant={running ? "default" : "secondary"}>
              {running ? "running" : "stopped"}
            </Badge>
            {progress?.circuitOpen || status?.circuit_open ? (
              <Badge variant="destructive">
                circuit open · {status?.circuit_cooldown_sec ?? 0}s
              </Badge>
            ) : null}
            {status?.regime_gate && !status?.regime_favorable ? (
              <Badge variant="destructive">
                regime risk-off{status?.regime_benchmark ? ` · ${status.regime_benchmark}` : ""}
              </Badge>
            ) : null}
            {connecting && open && !status && (
              <span className="text-xs text-muted-foreground">connecting…</span>
            )}
          </button>
        </div>
      </CardHeader>
      {open && (
        <CardContent className="space-y-4">
          {status?.regime_gate && !status?.regime_favorable && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              <span className="font-semibold">Market regime risk-off.</span>{" "}
              {status.regime_benchmark ?? "The benchmark"} is below its moving
              average — new setups are suppressed until the regime turns
              favorable (bear insurance).
            </div>
          )}
          {/* Progress bar + summary stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
            <div>
              <div className="text-muted-foreground">Universe</div>
              <div className="font-mono">
                {universeSize.toLocaleString()} symbols
              </div>
            </div>
            <div>
              <div className="text-muted-foreground">Scanned</div>
              <div className="font-mono">
                {scanned.toLocaleString()} / {universeSize.toLocaleString()}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground">Queue</div>
              <div className="font-mono">{queueDepth.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Hits ≥ {minScore}</div>
              <div className="font-mono">{status?.passed_hits ?? 0}</div>
            </div>
          </div>

          <div className="h-2 w-full overflow-hidden rounded bg-muted">
            <div
              className="h-full bg-emerald-500 transition-all"
              style={{
                width: `${universeSize > 0 ? Math.min(100, (scanned / universeSize) * 100) : 0}%`,
              }}
            />
          </div>

          {progress?.current && (
            <p className="text-xs text-muted-foreground font-mono">
              scanning: {progress.current} · last tick {new Date(progress.lastTickAt).toLocaleTimeString()}
            </p>
          )}

          {/* Min-score slider */}
          <div className="flex items-center gap-3">
            <Label htmlFor="scanner-min-score" className="text-xs">
              Min OverallScore
            </Label>
            <Input
              id="scanner-min-score"
              type="number"
              min={-7}
              max={7}
              step={1}
              value={minScore}
              onChange={(e) => {
                const n = Number(e.target.value);
                if (Number.isFinite(n)) setMinScore(n);
              }}
              onBlur={() => void handleMinScoreSave(minScore)}
              className="w-20 font-mono"
            />
            <span className="text-xs text-muted-foreground">
              Rows at or above this score trigger hit events and appear in the table.
            </span>
          </div>

          {(actionError || lastError) && (
            <p className="text-xs text-rose-500">{actionError ?? lastError}</p>
          )}

          {!running && (
            <p className="text-xs text-muted-foreground">
              Scanner is currently stopped. Ask an admin to start it
              from the <code>/admin</code> page if you need fresh
              setups.
            </p>
          )}

          {/* Results table */}
          {loading ? (
            <p className="text-xs text-muted-foreground">Loading…</p>
          ) : mergedItems.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No setups yet. The scanner will populate this table as it crawls.
            </p>
          ) : (
            <ScrollableScannerTable
              items={mergedItems}
              watchlist={settings?.DefaultWatchlist ?? []}
              onPromote={onPromote}
              onSelect={onSelect}
            />
          )}
        </CardContent>
      )}
    </Card>
  );
}

interface ScannerTableProps {
  items: ScannerItem[];
  /** Current default_watchlist tickers. A scanner row whose ticker is
   *  already in this set renders an "on watchlist" badge instead of
   *  the Add button, so the user can't re-add an existing name and
   *  can visually confirm their manual picks are still passing the
   *  scanner's live gate. Empty array is the safe default when
   *  settings haven't loaded yet. */
  watchlist: string[];
  onPromote: (ticker: string) => Promise<void>;
  onSelect?: (ticker: string) => void;
}

function ScrollableScannerTable({ items, watchlist, onPromote, onSelect }: ScannerTableProps) {
  const [promoting, setPromoting] = useState<string | null>(null);
  const [promoted, setPromoted] = useState<Record<string, boolean>>({});
  // Upper-cased set for O(1) membership lookup. The default_watchlist
  // is stored as an upper-case array on the server, but being defensive
  // here means a lower-case entry from a hand-edited config.yaml still
  // matches. useMemo keeps the set stable across re-renders unless
  // settings actually changed.
  const watchlistSet = useMemo(
    () => new Set(watchlist.map((t) => t.toUpperCase())),
    [watchlist],
  );

  const handlePromote = async (ticker: string) => {
    setPromoting(ticker);
    try {
      await onPromote(ticker);
      setPromoted((p) => ({ ...p, [ticker]: true }));
    } finally {
      setPromoting(null);
    }
  };

  // Inlined raw <table> instead of the shadcn <Table> wrapper because
  // Table introduces its own `overflow-x-auto` container, and sticky
  // positioning attaches to the nearest scroll ancestor — nesting an
  // overflow-auto wrapper around Table puts the sticky header inside
  // the inner container, where it scrolls with the body instead of
  // pinning to the outer max-h. `border-separate` is required for
  // `sticky` on <th> to visually pin on browsers that don't respect
  // sticky on border-collapsed cells.
  const stickyHead =
    "sticky top-0 z-10 bg-card h-10 px-2 text-left align-middle font-medium text-muted-foreground whitespace-nowrap";
  return (
    <div className="max-h-[480px] overflow-auto rounded border">
      <table className="w-full caption-bottom text-sm border-separate border-spacing-0">
        <thead>
          <TableRow>
            <th className={stickyHead}>Ticker</th>
            <th className={`${stickyHead} text-right`}>Score</th>
            <th className={`${stickyHead} text-right`}>Struct</th>
            <th className={`${stickyHead} text-right`}>Trend</th>
            <th className={`${stickyHead} text-right`}>Price</th>
            <th className={`${stickyHead} text-right`}>Pos</th>
            <th className={`${stickyHead} text-right`}>Ret%</th>
            <th className={`${stickyHead} text-right`}>R:R</th>
            <th className={`${stickyHead} text-right`}>Hit</th>
            <th className={`${stickyHead} text-right`}>ATR%</th>
            <th
              className={`${stickyHead} text-right`}
              title="20-day average close × volume (liquidity)"
            >
              $Vol
            </th>
            <th className={`${stickyHead} text-right`}>RS</th>
            <th className={stickyHead}>Last scan</th>
            <th className={stickyHead} />
          </TableRow>
        </thead>
        <tbody>
          {items.map((it) => {
            const tier = tickerTier(it.overall_score, it.hit_rate);
            const trend = (it.trend || "unknown") as
              | "up"
              | "down"
              | "neutral"
              | "unknown";
            // Range width derived the same way the main RankTable
            // computes it: percent distance from support to
            // resistance, used as the "of yy%" suffix on the Pos
            // column so the two tables read the same way.
            const rangePct =
              it.support && it.resistance && it.support > 0
                ? (it.resistance - it.support) / it.support
                : 0;
            return (
              <TableRow
                key={it.ticker}
                onClick={onSelect ? () => onSelect(it.ticker) : undefined}
                className={onSelect ? "cursor-pointer" : undefined}
              >
                <TableCell className={`font-mono font-semibold ${tierClass(tier)}`}>
                  {it.ticker}
                  <QualityFlagsPill flags={it.quality_flags} />
                </TableCell>
                <TableCell className="text-right font-mono">
                  {it.overall_score > 0 ? `+${it.overall_score}` : it.overall_score}
                </TableCell>
                <TableCell className="text-right font-mono">
                  {it.structural_score > 0 ? `+${it.structural_score}` : it.structural_score}
                </TableCell>
                <TableCell className={`text-right font-mono ${trendClass(trend)}`}>
                  {TREND_GLYPHS[trend]}
                </TableCell>
                <TableCell className="text-right font-mono">
                  {it.current_price !== null ? price(it.current_price) : "–"}
                </TableCell>
                <TableCell className="text-right font-mono">
                  <span className={posClass(it.current_position)}>
                    {percent(it.current_position)}
                  </span>{" "}
                  <span className="text-muted-foreground">of {percent(rangePct)}</span>
                </TableCell>
                <TableCell className={`text-right font-mono ${retClass(it.expected_return_pct)}`}>
                  {percent(it.expected_return_pct, 1)}
                </TableCell>
                <TableCell className={`text-right font-mono ${rrClass(it.reward_risk)}`}>
                  {ratio(it.reward_risk)}
                </TableCell>
                <TableCell className={`text-right font-mono ${hitClass(it.hit_rate)}`}>
                  {percent(it.hit_rate)}
                </TableCell>
                <TableCell className={`text-right font-mono ${atrClass(it.atr_pct)}`}>
                  {percent(it.atr_pct, 1)}
                </TableCell>
                <TableCell
                  className={
                    // Muted by default — scanner rows that pass the
                    // gate are already above the $1M floor, so there's
                    // no "flag below threshold" case here (unlike the
                    // main table where manually-added thin names slip
                    // through). A post-fix scanner should never render
                    // a dash in this column; if one shows up, it's a
                    // cached pre-liquidity row the rescan loop hasn't
                    // touched yet.
                    "text-right font-mono text-muted-foreground"
                  }
                >
                  {dollarCompact(it.avg_dollar_volume)}
                </TableCell>
                <TableCell className={`text-right font-mono ${rsClass(it.relative_strength)}`}>
                  {signedPercent(it.relative_strength)}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground font-mono whitespace-nowrap">
                  {new Date(it.last_scan).toLocaleString()}
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  {watchlistSet.has(it.ticker.toUpperCase()) ? (
                    <span
                      className="text-xs text-emerald-600/80"
                      title="Already on watchlist"
                    >
                      ✓ on watchlist
                    </span>
                  ) : promoted[it.ticker] ? (
                    <span className="text-xs text-emerald-500">added</span>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={promoting === it.ticker}
                      onClick={() => void handlePromote(it.ticker)}
                    >
                      {promoting === it.ticker ? "…" : "Add"}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

